import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import { TextField } from "@mui/material";
import ReactSelect from "react-select";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { allocateCoinSupply, getUsersSearch } from "../../../service/api";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(500px, 95vw)",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
};

const AllocateCoinSupplyModal = ({ isOpen, coin, onClose, onSuccess }) => {
    const [userOptions, setUserOptions] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [userLoading, setUserLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [quantity, setQuantity] = useState("");
    const [quantityError, setQuantityError] = useState("");
    const [loading, setLoading] = useState(false);

    // Debounced user search
    useEffect(() => {
        if (!userSearch) { setUserOptions([]); return; }
        const timer = setTimeout(() => {
            setUserLoading(true);
            getUsersSearch({ search: userSearch, sizePerPage: 20 })
                .then((res) => {
                    const list = res?.data?.result || [];
                    setUserOptions(
                        list.map((u) => ({
                            value: u._id,
                            label: `${[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"} (${u.email || ""})`,
                        }))
                    );
                })
                .catch(() => setUserOptions([]))
                .finally(() => setUserLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [userSearch]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedUser(null);
            setUserSearch("");
            setUserOptions([]);
            setQuantity("");
            setQuantityError("");
        }
    }, [isOpen]);

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        setQuantity(val);
        const num = Number(val);
        if (val !== "" && (!num || num <= 0)) {
            setQuantityError("Quantity must be greater than 0");
        } else if (coin?.totalSupply && num > coin.totalSupply) {
            setQuantityError(`Cannot exceed Total Supply (${Number(coin.totalSupply).toLocaleString()})`);
        } else {
            setQuantityError("");
        }
    };

    const handleAllocate = async () => {
        if (!selectedUser) {
            toast.error("Please select a user to allocate supply to");
            return;
        }
        const qty = Number(quantity);
        if (!qty || qty <= 0) {
            setQuantityError("Quantity must be greater than 0");
            return;
        }
        if (coin?.totalSupply && qty > coin.totalSupply) {
            setQuantityError(`Cannot exceed Total Supply (${Number(coin.totalSupply).toLocaleString()})`);
            return;
        }

        setLoading(true);
        try {
            const res = await allocateCoinSupply(coin._id, { userId: selectedUser.value, quantity: qty });
            if (res?.status) {
                toast.success(res?.message || "Supply allocated successfully", { toastId: "coin-supply-allocated" });
                if (onSuccess) onSuccess();
                onClose();
            } else {
                toast.error(res?.message || "Allocation failed", { toastId: "coin-supply-error" });
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Allocation failed", { toastId: "coin-supply-error" });
        } finally {
            setLoading(false);
        }
    };

    if (!coin) return null;

    const canSubmit = selectedUser && quantity !== "" && !quantityError && Number(quantity) > 0;

    return (
        <Modal open={!!isOpen} onClose={loading ? undefined : onClose}>
            <Box sx={modalStyle}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 700 }}>Allocate Supply — {coin?.symbol}</h5>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>One-time action · cannot be undone</div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", lineHeight: 1, color: "#6b7280", padding: 0 }}
                    >×</button>
                </div>

                {/* Coin info */}
                <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ color: "#0369a1" }}>Coin</span>
                        <span style={{ fontWeight: 600 }}>{coin?.name} ({coin?.symbol})</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: coin?.maxSupply > 0 ? 4 : 0 }}>
                        <span style={{ color: "#0369a1" }}>Total Supply</span>
                        <span style={{ fontWeight: 600 }}>{Number(coin?.totalSupply).toLocaleString()}</span>
                    </div>
                    {coin?.maxSupply > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#0369a1" }}>Max Supply</span>
                            <span style={{ fontWeight: 600 }}>{Number(coin?.maxSupply).toLocaleString()}</span>
                        </div>
                    )}
                </div>

                {/* Form */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
                    {/* User */}
                    <div>
                        <InputLabel style={{ fontSize: 13, marginBottom: 6 }}>Allocate to User *</InputLabel>
                        <ReactSelect
                            options={userOptions}
                            value={selectedUser}
                            onInputChange={(val) => setUserSearch(val)}
                            onChange={(opt) => setSelectedUser(opt)}
                            placeholder="Search by name or email…"
                            isSearchable
                            isClearable
                            isLoading={userLoading}
                            isDisabled={loading}
                            noOptionsMessage={() =>
                                userSearch ? "No users found" : "Type to search users"
                            }
                        />
                    </div>

                    {/* Quantity */}
                    <div>
                        <InputLabel style={{ fontSize: 13, marginBottom: 6 }}>
                            Quantity *
                            <small style={{ marginLeft: 8, color: "#6b7280", fontWeight: 400 }}>
                                max {Number(coin?.totalSupply).toLocaleString()}
                            </small>
                        </InputLabel>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: coin?.totalSupply }}
                            placeholder={`e.g. ${Number(coin?.totalSupply).toLocaleString()}`}
                            value={quantity}
                            error={!!quantityError}
                            onChange={handleQuantityChange}
                            disabled={loading}
                            onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                            }}
                        />
                        {quantityError && (
                            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{quantityError}</div>
                        )}
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                            This quantity will be credited to the user's Funding Wallet and set as the Circulating Supply.
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleAllocate} disabled={loading || !canSubmit}>
                        {loading ? <Spinner size="sm" /> : "Confirm Allocation"}
                    </button>
                </div>
            </Box>
        </Modal>
    );
};

export default AllocateCoinSupplyModal;
